import { useState, type MouseEvent } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/auth/permissions';
import type { PermissionKey } from '@/types/permissions';
import { paths } from '@/routes/paths';

interface QuickCreateAction {
  label: string;
  permission: PermissionKey;
  path: string;
}

/** Curated "create" shortcuts; the destination opens its create flow via ?create=1. */
const ACTIONS: QuickCreateAction[] = [
  { label: 'New page', permission: 'pages:create', path: `${paths.pages}?create=1` },
  { label: 'New blog post', permission: 'blog:create', path: `${paths.blog}?create=1` },
  { label: 'New service', permission: 'services:create', path: `${paths.services}?create=1` },
  { label: 'New lead', permission: 'leads:create', path: `${paths.leads}?create=1` },
  { label: 'New form', permission: 'forms:create', path: `${paths.forms}?create=1` },
  { label: 'Invite user', permission: 'users:create', path: `${paths.users}?create=1` },
];

export function QuickCreateMenu() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { can } = usePermissions();

  const available = ACTIONS.filter((a) => can(a.permission));
  if (available.length === 0) return null;

  return (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
      >
        Create
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {available.map((a) => (
          <MenuItem
            key={a.label}
            onClick={() => {
              setAnchor(null);
              navigate(a.path);
            }}
          >
            {a.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
